/**
 * №16.1 Stage C — the author-mode canvas (the locked greenfield trigger model).
 * ONE START · triggers everywhere · pull-not-push · option-context authoring ·
 * standing lane. Renders the CONSTRAINED model (it cannot draw an invalid
 * workflow) and every gesture emits a patch batch through actions.patch — the
 * SAME door the MCP author path uses (W23 / D-AU3). Presentational + controlled;
 * data + the patch mutation live in the container.
 */
import type { CSSProperties, ReactNode } from "react";
import type { SegmentView, StepView, TemplateDetail, WfPatch } from "../../hooks/use-workflow-templates";
import {
  buildSpine, triggerLabel, verbGlyph, assigneeLabel, outcomeColor, outcomeWash,
  isStandingSegment, branchTail, type SpineNode, type SpineDecision, type SpineBand,
} from "./wf-author-shared";

export interface Selection { kind: "segment" | "step" | "decision" | "outcome"; segmentNo: number; stepNo?: number; outcomeCode?: string }
export interface TemplateActions { patch: (patches: WfPatch[]) => void; busy: boolean; editable: boolean; notify?: (msg: string) => void }

interface Props {
  detail: TemplateDetail;
  selected: Selection | null;
  onSelect: (s: Selection | null) => void;
  picked: Record<number, string>;
  onPick: (segNo: number, code: string) => void;
  actions: TemplateActions;
}

const isSel = (s: Selection | null, kind: Selection["kind"], segNo: number, stepNo?: number) =>
  !!s && s.kind === kind && s.segmentNo === segNo && (stepNo == null || s.stepNo === stepNo);

export function TemplateCanvas({ detail, selected, onSelect, picked, onPick, actions }: Props) {
  const segments = detail.segments;
  const spine = buildSpine(segments);
  const nextSegNo = () => Math.max(0, ...segments.map((s) => s.segmentNo)) + 10;
  const nextStepNo = () => Math.max(0, ...segments.flatMap((s) => s.steps.map((x) => x.stepNo))) + 10;
  const nextDisplay = () => Math.max(0, ...segments.map((s) => s.displayOrder)) + 10;

  const addStep = (segNo: number) => actions.patch([{ op: "addStep", stepNo: nextStepNo(), segmentNo: segNo, name: "New step", action: "humanTask" }]);
  // A segment that opens after a whole level completes (one tail = a plain sequence; many tails =
  // a convergence after a concurrent band). Causal ordering (buildSpine) places it correctly — it
  // no longer matters that displayOrder is highest (that was the "renders at the bottom" bug).
  const addAfterTails = (tails: number[]) => {
    const no = nextSegNo();
    actions.patch([
      { op: "addSegment", segmentNo: no, name: "New segment", triggerKind: "afterSegments", displayOrder: nextDisplay() },
      ...tails.map((dep) => ({ op: "addSegmentDependency" as const, segmentNo: no, dependsOn: dep })),
    ]);
    onSelect({ kind: "segment", segmentNo: no });
  };
  // #1/#4 — the start is no longer special: add a segment that OPENS WITH THE MATTER (joins the
  // start band). The universal "add at the start / beside the concurrent heads" gesture.
  const addAtStart = () => {
    const no = nextSegNo();
    actions.patch([{ op: "addSegment", segmentNo: no, name: "New segment", triggerKind: "withMatter", displayOrder: nextDisplay() }]);
    onSelect({ kind: "segment", segmentNo: no });
  };
  // #4 — add a CONCURRENT sibling beside `seg`: same open-point (same trigger + same deps), so it
  // lands in the same band. Works at every level, not just the start.
  const addBeside = (seg: SegmentView) => {
    const no = nextSegNo();
    const base: WfPatch = { op: "addSegment", segmentNo: no, name: "New segment", triggerKind: seg.triggerKind, displayOrder: nextDisplay() };
    if (seg.triggerKind === "afterDecision") { base.triggerSegmentNo = seg.triggerSegmentNo; base.triggerOutcomeCode = seg.triggerOutcomeCode; }
    actions.patch([base, ...seg.dependsOn.map((dep) => ({ op: "addSegmentDependency" as const, segmentNo: no, dependsOn: dep }))]);
    onSelect({ kind: "segment", segmentNo: no });
  };
  const addBranchSegment = (decSegNo: number, outcome: string) => {
    const no = nextSegNo();
    actions.patch([{ op: "addSegment", segmentNo: no, name: "New segment", triggerKind: "afterDecision", triggerSegmentNo: decSegNo, triggerOutcomeCode: outcome, displayOrder: nextDisplay() }]);
    onSelect({ kind: "segment", segmentNo: no });
  };
  const addOutcome = (decSegNo: number, count: number) => {
    const code = `OPT${count + 1}`;
    actions.patch([{ op: "addOutcome", segmentNo: decSegNo, outcomeCode: code, label: "New outcome", isDefault: count === 0 }]);
    onPick(decSegNo, code);
  };
  // A6 (r8 §1.1) — insert a segment BETWEEN two currently linked (B opens after A). N opens after
  // A, and B is re-pointed to open after N — else N is orphaned or B races it. ONLY the A→B edge
  // moves; B's other dependencies are untouched. The re-point is ANNOUNCED, never silent.
  const insertBetween = (afterSeg: number, beforeSeg: number) => {
    const no = nextSegNo();
    const beforeName = segments.find((s) => s.segmentNo === beforeSeg)?.name ?? `Segment ${beforeSeg}`;
    actions.patch([
      { op: "addSegment", segmentNo: no, name: "New segment", triggerKind: "afterSegments", displayOrder: nextDisplay() },
      { op: "addSegmentDependency", segmentNo: no, dependsOn: afterSeg },
      { op: "removeSegmentDependency", segmentNo: beforeSeg, dependsOn: afterSeg },
      { op: "addSegmentDependency", segmentNo: beforeSeg, dependsOn: no },
    ]);
    actions.notify?.(`"${beforeName}" now opens after "New segment".`);
    onSelect({ kind: "segment", segmentNo: no });
  };
  // A4 (r8 §2.3) — one gesture that creates a segment and wires it to ALL of a decision's branch
  // tails with an ALL join. The author never reasons about join modes to rejoin a branch.
  const addRejoin = (dec: SpineDecision) => {
    const tails = [...new Set(dec.branches.flatMap((b) => branchTail(b.nodes)))];
    if (tails.length === 0) { actions.notify?.("No branch has any work yet — add a segment on a path first, then rejoin."); return; }
    const no = nextSegNo();
    actions.patch([
      { op: "addSegment", segmentNo: no, name: "After the decision", triggerKind: "afterSegments", joinMode: "all", displayOrder: nextDisplay() },
      ...tails.map((dep) => ({ op: "addSegmentDependency" as const, segmentNo: no, dependsOn: dep })),
    ]);
    onSelect({ kind: "segment", segmentNo: no });
  };

  // ---- step row -----------------------------------------------------------
  const stepRow = (seg: SegmentView, st: StepView, ord?: number) => (
    <button key={st.stepNo} className={`wfa-step ${st.required ? "required" : ""} ${isSel(selected, "step", seg.segmentNo, st.stepNo) ? "sel" : ""}`}
      onClick={(e) => { e.stopPropagation(); onSelect({ kind: "step", segmentNo: seg.segmentNo, stepNo: st.stepNo }); }}>
      {ord != null && <span className="ord" aria-hidden="true">{ord}.</span>}
      <span className="req" title={st.required ? "required" : "optional"}>{st.required ? "●" : "○"}</span>
      <span className="vrb" title={st.action}>{verbGlyph(st.action)}</span>
      <span className="nm">{st.name}</span>
      {st.binding && <span className="chip" title={`waits · ${st.binding.eventKey || st.binding.kind}`}>◷</span>}
      {st.conditions.length > 0 && <span className="chip" title="applies when…">?</span>}
      {st.block && <span className="chip block" title={`from block ${st.block.code} v${st.block.version}`}>❏</span>}
      <span className="who">{assigneeLabel(st)}</span>
    </button>
  );

  // ---- segment card -------------------------------------------------------
  const segCard = (seg: SegmentView, inBand: boolean) => {
    const gridOne = inBand || seg.steps.length <= 3; // band cells are narrow → one column
    const conv = seg.dependsOn.length > 1;
    return (
      <div className={`wfa-seg ${isSel(selected, "segment", seg.segmentNo) ? "sel" : ""}`} onClick={() => onSelect({ kind: "segment", segmentNo: seg.segmentNo })}>
        <div className="wfa-seg__h">
          <span className="wfa-seg__nm">{seg.name}</span>
          {!inBand && <span className="wfa-trg" title={triggerLabel(seg, segments)}><span className="k">opens: </span>{triggerLabel(seg, segments)}</span>}
        </div>
        <div className={`wfa-steps ${gridOne ? "one" : ""}`}>
          {seg.steps.map((st, i) => stepRow(seg, st, seg.stepsOrdered ? i + 1 : undefined))}
          {actions.editable && <div className="wfa-addstep"><button className="wfa-dashbtn" onClick={(e) => { e.stopPropagation(); addStep(seg.segmentNo); }}>+ step</button></div>}
        </div>
        {conv && <div className="wfa-conv"><span className="s">★</span> convergence — opens after {seg.dependsOn.length} branches settle</div>}
      </div>
    );
  };

  // ---- decision card + branch zones --------------------------------------
  const decCard = (seg: SegmentView) => {
    const outcomes = seg.outcomes;
    const chosen = picked[seg.segmentNo] ?? outcomes.find((o) => o.isDefault)?.code ?? outcomes[0]?.code ?? "";
    const idx = Math.max(0, outcomes.findIndex((o) => o.code === chosen));
    const c = outcomeColor(idx), cw = outcomeWash(idx);
    const dec = (
      <div className={`wfa-dec ${isSel(selected, "decision", seg.segmentNo) ? "sel" : ""}`} onClick={() => onSelect({ kind: "decision", segmentNo: seg.segmentNo })}>
        <div className="wfa-dec__h">
          <span className="wfa-dmk"><i /></span>
          <span className="wfa-dec__t"><span className="q">{seg.decisionName || seg.name}</span><span className="s"> · opens {triggerLabel(seg, segments)}</span></span>
        </div>
        <div className="wfa-osel">
          {outcomes.map((o, i) => (
            <button key={o.code} className={`wfa-ob ${o.code === chosen ? "sel" : ""}`} style={{ ["--wfa-oc" as string]: outcomeColor(i) } as CSSProperties}
              title={`${o.label} — ${o.code}${o.isDefault ? " · default" : ""}`}
              onClick={(e) => { e.stopPropagation(); onPick(seg.segmentNo, o.code); }}>
              <span className="ol">{o.label}</span>
              <span className="oc">{o.code}{o.isDefault ? " · default" : ""}</span>
            </button>
          ))}
          {actions.editable && <button className="wfa-addopt" title="add outcome" onClick={(e) => { e.stopPropagation(); addOutcome(seg.segmentNo, outcomes.length); }}>+</button>}
        </div>
      </div>
    );
    return { dec, chosen, idx, c, cw };
  };

  // ---- render one level: a segment, a decision, or a concurrency band -----
  function renderBand(band: SpineBand, keyPrefix: string, nested: boolean): ReactNode {
    const first = band.members[0]!.seg;
    const label = first.triggerKind === "withMatter" ? "open with the matter" : `open ${triggerLabel(first, segments)}`;
    return (
      <div key={keyPrefix} className="wfa-band">
        <div className="wfa-band__hd"><b>◎ {band.members.length} concurrent</b> · {label}</div>
        <div className="wfa-band__row">
          {band.members.map((m) => <div key={m.seg.segmentNo}>{m.kind === "decision" ? renderDecision(m, `bd-${m.seg.segmentNo}`, nested) : segCard(m.seg, true)}</div>)}
        </div>
        {actions.editable && <div className="wfa-ins"><button className="wfa-dashbtn" onClick={() => addBeside(first)}>+ concurrent segment</button></div>}
      </div>
    );
  }

  function renderDecision(node: SpineDecision, keyPrefix: string, nested: boolean): ReactNode {
    const { dec, chosen, c, cw } = decCard(node.seg);
    const br = node.branches.find((b) => b.code === chosen) ?? node.branches[0];
    const zoneStyle = { ["--wfa-oc" as string]: c, ["--wfa-ocw" as string]: cw } as CSSProperties;
    const inner = br
      ? (br.nodes.length
        ? renderNodes(br.nodes, true)
        : (actions.editable ? <div className="wfa-bempty">This path has no work yet.<div><button className="wfa-dashbtn" onClick={() => addBranchSegment(node.seg.segmentNo, br.code)}>+ first segment on this path</button></div></div> : <div className="wfa-bempty">No work on this path.</div>))
      : null;
    return (
      <div key={keyPrefix}>
        {dec}
        <div className={nested ? "wfa-subband" : "wfa-bzone"} style={zoneStyle}>
          <div className={nested ? "wfa-subcrumb" : "wfa-bcrumb"}>
            <span className="tag">{(node.seg.decisionName || node.seg.name)} → {br?.label ?? chosen}</span>
            {actions.editable && br && <button className="x" onClick={() => onSelect({ kind: "outcome", segmentNo: node.seg.segmentNo, outcomeCode: br.code })}>edit outcome</button>}
          </div>
          {inner}
          {actions.editable && br && br.nodes.length > 0 && (
            <div className="wfa-ins"><button className="wfa-dashbtn" onClick={() => addBranchSegment(node.seg.segmentNo, br.code)}>+ segment on this path</button></div>
          )}
        </div>
        {/* A4 — one gesture to converge: a segment wired to ALL branch tails (ALL join). */}
        {actions.editable && node.branches.some((b) => b.nodes.length > 0) && (
          <div className="wfa-ins"><button className="wfa-dashbtn" onClick={() => addRejoin(node)}>+ segment where all paths rejoin</button></div>
        )}
      </div>
    );
  }

  function renderLevel(node: SpineNode, keyPrefix: string, nested: boolean): ReactNode {
    if (node.kind === "band") return renderBand(node, keyPrefix, nested);
    if (node.kind === "decision") return renderDecision(node, keyPrefix, nested);
    return <div key={keyPrefix}>{segCard(node.seg, false)}</div>;
  }

  // ---- walk a level-list, interleaving ALWAYS-VISIBLE add slots -----------
  function renderNodes(nodes: SpineNode[], nested: boolean): ReactNode[] {
    const out: ReactNode[] = [];
    // #1/#4 — the start is not special: an add slot BEFORE the first level (trunk only, non-empty).
    if (!nested && actions.editable && nodes.length > 0) {
      out.push(<div key="add-start" className="wfa-ins"><button className="wfa-dashbtn" onClick={addAtStart}>+ segment at the start</button></div>);
      out.push(<div key="lk-start" className="wfa-link" />);
    }
    nodes.forEach((node, i) => {
      if (i > 0) out.push(<div key={`lk-${i}`} className="wfa-link" />);
      // B4 — insert-between two SINGLE linked segments (now anywhere, incl. inside branches).
      if (actions.editable && i > 0) {
        const prev = nodes[i - 1]!;
        if (prev.kind === "segment" && node.kind === "segment" && node.seg.dependsOn.includes(prev.seg.segmentNo)) {
          const aNo = prev.seg.segmentNo, bNo = node.seg.segmentNo;
          out.push(<div key={`ib-${i}`} className="wfa-ins"><button className="wfa-dashbtn" onClick={() => insertBetween(aNo, bNo)}>+ segment here</button></div>);
        }
      }
      out.push(renderLevel(node, `lv-${nested ? "n" : "t"}-${i}`, nested));
      // after / beside slots — universal, one row per level.
      if (actions.editable) {
        if (node.kind === "segment") {
          out.push(
            <div key={`add-${i}`} className="wfa-ins wfa-ins--row">
              <button className="wfa-dashbtn" onClick={() => addBeside(node.seg)}>+ concurrent</button>
              <button className="wfa-dashbtn" onClick={() => addAfterTails([node.seg.segmentNo])}>+ segment</button>
            </div>,
          );
        } else if (node.kind === "band") {
          out.push(<div key={`add-${i}`} className="wfa-ins"><button className="wfa-dashbtn" onClick={() => addAfterTails(node.members.map((m) => m.seg.segmentNo))}>+ segment after all</button></div>);
        } else {
          out.push(<div key={`add-${i}`} className="wfa-ins"><button className="wfa-dashbtn" onClick={() => addAfterTails([node.seg.segmentNo])}>+ segment</button></div>);
        }
      }
    });
    return out;
  }

  const standing = segments.filter(isStandingSegment);

  return (
    <div className={`wfa-canvas ${standing.length ? "with-lane" : ""}`}>
      <div className="wfa-main">
        <div className="wfa-start"><span className="r" /><b>{detail.template?.subjectType === "matter" ? "Matter opens" : "Anchor opens"}</b><span>· the single start</span></div>
        {spine.length > 0 && <div className="wfa-link" />}
        {renderNodes(spine, false)}
        {actions.editable && spine.length === 0 && (
          <div className="wfa-bempty" style={{ marginTop: 12 }}>No segments yet.<div><button className="wfa-dashbtn" onClick={() => actions.patch([{ op: "addSegment", segmentNo: nextSegNo(), name: "New segment", triggerKind: "withMatter", displayOrder: nextDisplay() }])}>+ first segment</button></div></div>
        )}
      </div>
      {standing.length > 0 && (
        <aside className="wfa-lane">
          <div className="wfa-lane__hd">Timed &amp; standing</div>
          <div className="wfa-lane__sub">a date, no causal trigger</div>
          {standing.map((s) => (
            <button key={s.segmentNo} className="wfa-litem" onClick={() => onSelect({ kind: "segment", segmentNo: s.segmentNo })}>
              <div className="n">{s.name}</div><div className="w">◷ {triggerLabel(s, segments)}</div>
            </button>
          ))}
        </aside>
      )}
    </div>
  );
}
